import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { BLOCK_REGISTRY, DEFAULT_LAYOUT } from './BlockRenderer';
import { investmentPerspectivesAPI } from '../../services/api';

const SECTION_META = {
  intro_text:                    { label: 'Introduction' },
  market_overview:               { label: 'Market Overview', hasCallouts: true },
  performance_review:            { label: 'Review', hasCallouts: true },
  outlook:                       { label: 'Outlook', hasCallouts: true },
};

/** Build the propagation rows (editable sections/blocks) from the source formData,
 * grouped under a non-selectable "Page N" header per report page. */
function buildRows(formData) {
  const rows = [{
    id: '__layout__',
    label: 'Whole Report Layout (all pages)',
    keys: ['page_layout', 'custom_blocks', 'heading_overrides', 'heading_styles'],
  }];

  const layout = formData.page_layout || DEFAULT_LAYOUT;
  const seen = new Set(['__layout__']);
  layout.forEach((page, pi) => {
    const pageRows = [];
    (page.blocks || []).forEach((b) => {
      const blockId = typeof b === 'string' ? b : b.id;
      if (seen.has(blockId)) return;
      const regEntry = BLOCK_REGISTRY[blockId];
      const customDef = (formData.custom_blocks || {})[blockId];

      if (blockId === 'title_box') {
        pageRows.push({ id: blockId, label: 'Title Box', keys: ['title_box'] });
      } else if (regEntry && regEntry.contentKey) {
        const keys = [regEntry.contentKey];
        if (SECTION_META[regEntry.contentKey]?.hasCallouts) keys.push(`${regEntry.contentKey}_callouts`);
        pageRows.push({ id: blockId, label: regEntry.label || blockId, keys });
      } else if (customDef && (customDef.type === 'text' || customDef.type === 'image')) {
        pageRows.push({ id: blockId, label: customDef.label || (customDef.type === 'image' ? 'Image' : blockId), keys: [`custom_content.${blockId}`] });
      } else {
        return;
      }
      seen.add(blockId);
    });
    // Every page gets a header and its own layout row — including pages built
    // purely from shapes/logos, which contribute no content rows of their own.
    // The per-page row is what lets one page's design (a tailored cover, say)
    // move without dragging the rest of the source's page structure with it.
    rows.push({ id: `__page_${pi + 1}__`, isHeader: true, label: `Page ${pi + 1}` });
    rows.push({
      id: `__layout_p${pi}__`,
      label: `↳ Page ${pi + 1} design (position + blocks)`,
      keys: [`page_layout.${pi}`],
    });
    rows.push(...pageRows);
  });

  return rows;
}

/**
 * Propagate content sections from the source (period, domain) to other brand
 * domains (and optionally a different month). Columns are DOMAINS, not portfolios.
 */
// The Investment Perspectives portal report only runs from July 2026 —
// earlier months exist in the data but were never produced here.
const FIRST_PERIOD = '2026-07';

const PropagateModal = ({ brands, period, domain, formData, onClose, onComplete }) => {
  const rows = useMemo(() => buildRows(formData), [formData]);
  // Page-header rows are visual grouping only — every select/propagate action
  // operates on the selectable rows.
  const selectableRows = useMemo(() => rows.filter((r) => !r.isHeader), [rows]);
  const sourceDomain = domain || '_default';

  const [months, setMonths] = useState([]);
  const [targetPeriod, setTargetPeriod] = useState(period);

  useEffect(() => {
    investmentPerspectivesAPI.getAvailableMonths()
      .then((res) => setMonths((res.months || []).map((m) => m.value).filter((m) => m >= FIRST_PERIOD)))
      .catch(() => {});
  }, []);

  const isCrossPeriod = targetPeriod !== period;

  // Target columns are TAILORED clients only. Non-tailored clients always mirror
  // the Drummond report via fallback, so they must never get their own content
  // row — propagating to them would break that mirror.
  const targetDomains = useMemo(() => {
    const tailored = (brands || [])
      .filter((b) => b.tailored)
      .map((b) => ({ domain: b.domain, label: b.company_name || b.domain }));
    return isCrossPeriod ? tailored : tailored.filter((d) => d.domain !== sourceDomain);
  }, [brands, isCrossPeriod, sourceDomain]);

  const [checked, setChecked] = useState({});
  useEffect(() => {
    const init = {};
    selectableRows.forEach((row) => {
      init[row.id] = {};
      targetDomains.forEach((d) => { init[row.id][d.domain] = false; });
    });
    setChecked(init);
  }, [selectableRows, targetDomains]);

  const [propagating, setPropagating] = useState(false);
  const [result, setResult] = useState(null);

  const toggleCell = useCallback((rowId, dom) => {
    setChecked((prev) => ({ ...prev, [rowId]: { ...prev[rowId], [dom]: !prev[rowId]?.[dom] } }));
  }, []);
  const toggleRow = useCallback((rowId) => {
    setChecked((prev) => {
      const allOn = targetDomains.every((d) => prev[rowId]?.[d.domain]);
      const next = {};
      targetDomains.forEach((d) => { next[d.domain] = !allOn; });
      return { ...prev, [rowId]: next };
    });
  }, [targetDomains]);
  const toggleColumn = useCallback((dom) => {
    setChecked((prev) => {
      const allOn = selectableRows.every((r) => prev[r.id]?.[dom]);
      const next = { ...prev };
      selectableRows.forEach((r) => { next[r.id] = { ...next[r.id], [dom]: !allOn }; });
      return next;
    });
  }, [selectableRows]);
  const toggleAll = useCallback(() => {
    setChecked((prev) => {
      const allOn = selectableRows.every((r) => targetDomains.every((d) => prev[r.id]?.[d.domain]));
      const next = {};
      selectableRows.forEach((r) => { next[r.id] = {}; targetDomains.forEach((d) => { next[r.id][d.domain] = !allOn; }); });
      return next;
    });
  }, [selectableRows, targetDomains]);

  const affected = useMemo(() => {
    const set = new Set();
    selectableRows.forEach((row) => targetDomains.forEach((d) => { if (checked[row.id]?.[d.domain]) set.add(d.domain); }));
    return set.size;
  }, [checked, selectableRows, targetDomains]);

  const handlePropagate = async () => {
    setPropagating(true);
    setResult(null);

    const perDomain = {};
    targetDomains.forEach((d) => {
      const keys = new Set();
      selectableRows.forEach((row) => { if (checked[row.id]?.[d.domain]) row.keys.forEach((k) => keys.add(k)); });
      if (keys.size > 0) perDomain[d.domain] = [...keys];
    });

    // Group domains by identical section sets to minimise API calls
    const groups = {};
    for (const [dom, keys] of Object.entries(perDomain)) {
      const k = keys.sort().join('|');
      if (!groups[k]) groups[k] = { sections: keys, domains: [] };
      groups[k].domains.push(dom);
    }

    let totalCount = 0;
    let allFailures = [];
    try {
      for (const group of Object.values(groups)) {
        const res = await investmentPerspectivesAPI.propagate({
          source_period: period,
          source_domain: sourceDomain,
          target_period: targetPeriod,
          target_domains: group.domains,
          sections: group.sections,
        });
        totalCount += res.count || 0;
        if (res.failures?.length) allFailures = [...allFailures, ...res.failures];
      }
      setResult({ success: true, count: totalCount, failures: allFailures });
      if (allFailures.length === 0 && onComplete) onComplete();
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || 'Error propagating content' });
    } finally {
      setPropagating(false);
    }
  };

  // The .ip-app wrapper keeps this body-portaled modal inside the IP style
  // scope (all IP CSS is prefixed with .ip-app to stop leaks into other apps).
  return createPortal(
    <div className="ip-app">
    <div className="propagate-modal-overlay" onClick={onClose}>
      <div className="propagate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="propagate-modal-header">
          <h3>Propagate Content</h3>
          <button className="propagate-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="propagate-modal-controls">
          <label className="propagate-quarter-selector">
            <span>Target Month</span>
            <select value={targetPeriod} onChange={(e) => setTargetPeriod(e.target.value)}>
              <option value={period}>{period} (current)</option>
              {months.filter((m) => m !== period).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <span className="propagate-cross-quarter-note">
            Copying from {sourceDomain === '_default' ? 'Drummond' : sourceDomain}
            {isCrossPeriod ? ` (${period} → ${targetPeriod})` : ''}.
          </span>
        </div>

        <p className="propagate-modal-desc">
          Copy sections to tailored clients. Non-tailored clients aren't listed — they
          always mirror the Drummond report automatically.
        </p>
        <p className="propagate-modal-desc propagate-layout-note">
          A <strong>Page N design</strong> row moves just that page — its block positions,
          the blocks on it and any image it holds — leaving every other page and all
          commentary as the target has it. Use it to put one report's cover on another
          report. <strong>Whole Report Layout</strong> replaces the target's entire page
          structure instead, so the target ends up with the source's pages.
        </p>

        {result && (
          <div className={`propagate-result ${result.success ? 'success' : 'error'}`}>
            {result.success
              ? `Propagated to ${result.count} brand${result.count !== 1 ? 's' : ''}.`
              : result.error}
            {result.failures?.length > 0 && (
              <div className="propagate-failures">Failed: {result.failures.map((f) => f.domain).join(', ')}</div>
            )}
          </div>
        )}

        <div className="propagate-grid-wrapper">
          <table className="propagate-grid">
            <thead>
              <tr>
                <th className="propagate-corner">
                  <button className="propagate-select-all" onClick={toggleAll} title="Toggle all">All</button>
                </th>
                {targetDomains.map((d) => (
                  <th key={d.domain} className="propagate-col-header">
                    <button className="propagate-col-toggle" onClick={() => toggleColumn(d.domain)} title="Toggle column">
                      {d.label}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (row.isHeader ? (
                <tr key={row.id} className="propagate-page-header">
                  <td colSpan={targetDomains.length + 1}>{row.label}</td>
                </tr>
              ) : (
                <tr key={row.id}>
                  <td className="propagate-row-label">
                    <button className="propagate-row-toggle" onClick={() => toggleRow(row.id)} title="Toggle row">
                      {row.label}
                    </button>
                  </td>
                  {targetDomains.map((d) => (
                    <td key={d.domain} className="propagate-cell">
                      <input
                        type="checkbox"
                        checked={!!checked[row.id]?.[d.domain]}
                        onChange={() => toggleCell(row.id, d.domain)}
                      />
                    </td>
                  ))}
                </tr>
              )))}
            </tbody>
          </table>
        </div>

        <div className="propagate-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-propagate" onClick={handlePropagate} disabled={propagating || affected === 0}>
            {propagating ? 'Propagating...' : `Propagate to ${affected} brand${affected !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
    </div>,
    document.body
  );
};

export default PropagateModal;
